/**
 * Shared UI component for UI Chart.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiChartSeriesPoint, UiChartVariant } from './ui-chart.types';

@Component({
  selector: 'ui-chart',
  standalone: true,
  imports: [CommonModule, TranslatePipe, UiLabelTranslatePipe],
  templateUrl: './ui-chart.component.html',
  styleUrl: './ui-chart.component.scss'
})
export class UiChartComponent {
  private static readonly FALLBACK_COLORS = [
    'var(--color-steel-blue)',
    'var(--color-gold)',
    'var(--color-success)',
    'var(--color-danger)',
    'color-mix(in srgb, var(--color-steel-blue) 48%, var(--color-card-bg))'
  ];

  readonly title = input('');
  readonly subtitle = input('');
  readonly variant = input<UiChartVariant>('bar');
  readonly series = input<UiChartSeriesPoint[]>([]);
  readonly totalValue = computed(() => this.series().reduce((sum, point) => sum + point.value, 0));
  readonly linePath = computed(() => {
    const points = this.series();

    if (points.length === 0) {
      return '';
    }

    const max = this.maxValue();

    if (points.length === 1) {
      const y = this.chartY(points[0].value, max);
      return `M 50 ${y} L 50 ${y}`;
    }

    return points
      .map((point, index) => {
        const x = this.chartX(index, points.length);
        const y = this.chartY(point.value, max);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');
  });
  readonly lineMarkers = computed(() => {
    const points = this.series();
    const max = this.maxValue();

    return points.map((point, index) => ({
      ...point,
      x: this.chartX(index, points.length),
      y: this.chartY(point.value, max),
      color: this.pointColor(point, index)
    }));
  });
  readonly pieGradient = computed(() => {
    const total = this.totalValue();

    if (total <= 0) {
      return 'conic-gradient(color-mix(in srgb, var(--color-border) 70%, var(--color-card-bg)) 0 100%)';
    }

    let start = 0;

    const stops = this.series().map((point, index) => {
      const span = (point.value / total) * 100;
      const end = start + span;
      const stop = `${this.pointColor(point, index)} ${start}% ${end}%`;
      start = end;
      return stop;
    });

    return `conic-gradient(${stops.join(', ')})`;
  });

  maxValue(): number {
    return this.series().reduce((max, point) => Math.max(max, point.value), 1);
  }

  pointColor(point: UiChartSeriesPoint, index: number): string {
    return point.color || UiChartComponent.FALLBACK_COLORS[index % UiChartComponent.FALLBACK_COLORS.length];
  }

  pointPercent(point: UiChartSeriesPoint): number {
    const total = this.totalValue();
    return total > 0 ? Math.round((point.value / total) * 100) : 0;
  }

  private chartX(index: number, totalPoints: number): number {
    if (totalPoints <= 1) {
      return 50;
    }

    return 8 + (index * 84) / (totalPoints - 1);
  }

  private chartY(value: number, max: number): number {
    if (max <= 0) {
      return 50;
    }

    return 92 - (value / max) * 74;
  }
}
